'use client';

import { useMutation } from '@apollo/client/react';
import { usePortalContext } from '@cloai/uikit';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/navigation';
import { memo, useMemo } from 'react';
import { toast } from 'react-toastify';
import { twMerge } from 'tailwind-merge';

import {
  GET_PUBLICATIONS_FOR_ME,
  SUBSCRIBE_USER,
  UNSUBSCRIBE_USER,
  GET_PROFILE,
  GET_USER,
  GET_ME_SUBSCRIPTIONS,
  GET_ME_SUBSCRIBERS,
  GET_SUBSCRIBERS,
  GET_SUBSCRIPTIONS,
} from '@/.graphql';
import { useProfileContext, useGTMContext } from '@/src/providers';

const styles = {
  button:
    'w-[153px] shrink-0 flex items-center justify-center h-12 text-base font-semibold disabled:cursor-not-allowed disabled:opacity-50',
  buttonSubscribe:
    'rounded-xl bg-fill-button hover:bg-fill-yellowFade80 text-label-black',
  buttonUnsubscribe: 'rounded-lg bg-fill-zinc800 hover:bg-fill-zinc700',
  name: 'w-[40px] h-[40px] cursor-pointer rounded-full bg-fill-rose hover:bg-fill-roseFade80 flex items-center justify-center text-xl font-semibold',
};

interface SubscriberProps {
  firstName: string | null;
  avatarUrl?: string;
  id: string;
  isSubscribed?: boolean;
}

export const Subscriber = memo(
  ({ firstName, id, isSubscribed }: SubscriberProps) => {
    const t = useTranslations('ProfilePage');
    const profile = useProfileContext();
    const router = useRouter();
    const { closePortal } = usePortalContext();
    const [subscribeUser, { loading: subscribing }] =
      useMutation(SUBSCRIBE_USER);
    const [unsubscribeUser, { loading: unsubscribing }] =
      useMutation(UNSUBSCRIBE_USER);
    const sendGTM = useGTMContext();

    const refetchQueries = useMemo(
      () => [
        { query: GET_PUBLICATIONS_FOR_ME },
        { query: GET_PROFILE },
        { query: GET_ME_SUBSCRIPTIONS },
        { query: GET_ME_SUBSCRIBERS },
        { query: GET_USER, variables: { userId: id } },
        { query: GET_SUBSCRIBERS, variables: { userId: id } },
        { query: GET_SUBSCRIPTIONS, variables: { userId: id } },
      ],
      [id],
    );

    const onSubscribeClick = async () => {
      try {
        await subscribeUser({
          variables: { publisherId: id },
          refetchQueries,
        });
        sendGTM({
          event: 'Subscribe_to_user',
          publisher: id,
        });
      } catch {
        toast.error(t('subscribeError'), {
          position: 'bottom-center',
          autoClose: 3000,
          theme: 'dark',
        });
      }
    };

    const onUnsubscribeClick = async () => {
      try {
        await unsubscribeUser({
          variables: { publisherId: id },
          refetchQueries,
        });
      } catch {
        toast.error(t('unsubscribeError'), {
          position: 'bottom-center',
          autoClose: 3000,
          theme: 'dark',
        });
      }
    };

    const handleAvatarClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      closePortal();

      if (id) {
        if (id === profile?.id) {
          router.push('/profile');
        } else {
          router.push(`/user/${id}`);
        }
      }
    };

    return (
      <div className="flex gap-3 items-center">
        <div className={styles.name} onClick={handleAvatarClick}>
          {firstName && firstName[0]}
        </div>
        <div className="grow truncate">{firstName}</div>

        {id !== profile?.id ? (
          !isSubscribed ? (
            <button
              className={twMerge(styles.button, styles.buttonSubscribe)}
              onClick={onSubscribeClick}
              disabled={subscribing}
            >
              {t('subscribe')}
            </button>
          ) : (
            <button
              className={twMerge(styles.button, styles.buttonUnsubscribe)}
              onClick={onUnsubscribeClick}
              disabled={unsubscribing}
            >
              {t('usnubscribe')}
            </button>
          )
        ) : null}
      </div>
    );
  },
);
Subscriber.displayName = 'Subscriber';
